import { useEffect, useMemo, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const EMPTY_COLUMN_FILTERS = {};

function cellText(row, col) {
  if (typeof col.searchValue === 'function') return String(col.searchValue(row) ?? '');
  if (col.sortValue) return String(col.sortValue(row) ?? '');
  if (col.key === 'actif') {
    return row.actif !== false && row.actif !== 0 ? 'Actif' : 'Inactif';
  }
  if (col.render) {
    const rendered = col.render(row);
    if (rendered == null || rendered === '') return '';
    if (
      typeof rendered === 'string' ||
      typeof rendered === 'number' ||
      typeof rendered === 'boolean'
    ) {
      return String(rendered);
    }
    // Nœud React : String(element) → "[object Object]" — utiliser la valeur brute
    const raw = row[col.key];
    return raw == null || raw === '' ? '' : String(raw);
  }
  const val = row[col.key];
  if (val == null || val === '') return '';
  return String(val);
}

function rowMatchesSearch(row, columns, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return columns.some((col) => {
    const text = cellText(row, col);
    return text.toLowerCase().includes(q);
  });
}

function rowMatchesActif(row, actifFilter) {
  if (actifFilter === 'all') return true;
  const active = row.actif !== false && row.actif !== 0;
  return actifFilter === 'active' ? active : !active;
}

/**
 * Recherche, filtres colonne, statut actif, tri et pagination côté client.
 */
export function useTableListState(
  records,
  {
    columns = [],
    filters = [],
    defaultPageSize = 15,
    defaultSortBy,
    defaultSortDir = 'asc',
    recordPredicate,
    initialColumnFilters = EMPTY_COLUMN_FILTERS,
    initialQuery = '',
    initialPage = 1,
    initialPageSize,
    initialSortBy: initialSortByOverride,
    initialSortDir: initialSortDirOverride,
    onPersistedStateChange,
  } = {},
) {
  const [query, setQuery] = useState(initialQuery);
  const [actifFilter, setActifFilter] = useState('all');
  const [columnFilters, setColumnFilters] = useState(() => initialColumnFilters);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize ?? defaultPageSize);
  const [sortBy, setSortBy] = useState(initialSortByOverride || defaultSortBy || columns[0]?.key || 'id');
  const [sortDir, setSortDir] = useState(initialSortDirOverride ?? defaultSortDir);

  const filterSelectData = useMemo(() => {
    const data = {};
    for (const f of filters) {
      const values = new Set();
      for (const row of records) {
        const col = columns.find((c) => c.key === f.key);
        const text = col ? cellText(row, col) : row[f.key];
        if (text) values.add(text);
      }
      data[f.key] = [...values].sort((a, b) => a.localeCompare(b, 'fr')).map((v) => ({
        value: v,
        label: v,
      }));
    }
    return data;
  }, [records, columns, filters]);

  const filtered = useMemo(() => {
    let list = recordPredicate ? records.filter(recordPredicate) : [...records];
    list = list.filter(
      (row) =>
        rowMatchesSearch(row, columns, query) &&
        rowMatchesActif(row, actifFilter),
    );

    for (const f of filters) {
      const val = columnFilters[f.key];
      if (!val) continue;
      list = list.filter((row) => {
        const col = columns.find((c) => c.key === f.key);
        const text = col ? cellText(row, col) : row[f.key];
        return text === val;
      });
    }

    const col = columns.find((c) => c.key === sortBy);
    if (col) {
      list = [...list].sort((a, b) => {
        const av = cellText(a, col).toLowerCase();
        const bv = cellText(b, col).toLowerCase();
        const cmp = av.localeCompare(bv, 'fr', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return list;
  }, [records, columns, query, actifFilter, columnFilters, filters, sortBy, sortDir, recordPredicate]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  useEffect(() => {
    setColumnFilters(initialColumnFilters);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync alertes dashboard
  }, [JSON.stringify(initialColumnFilters)]);

  useEffect(() => {
    setQuery(initialQuery);
    setPage(1);
  }, [initialQuery]);

  useEffect(() => {
    onPersistedStateChange?.({
      query,
      actifFilter,
      columnFilters,
      page,
      pageSize,
      sortBy,
      sortDir,
    });
  }, [query, actifFilter, columnFilters, page, pageSize, sortBy, sortDir, onPersistedStateChange]);

  useEffect(() => {
    setPage(1);
  }, [query, actifFilter, columnFilters, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const setColumnFilter = (key, value) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const resetFilters = () => {
    setQuery('');
    setActifFilter('all');
    setColumnFilters({});
    setPage(1);
  };

  const hasActiveFilters =
    query.trim() !== '' ||
    actifFilter !== 'all' ||
    Object.keys(columnFilters).length > 0;

  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  return {
    query,
    setQuery,
    actifFilter,
    setActifFilter,
    columnFilters,
    setColumnFilter,
    filterSelectData,
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    sortBy,
    sortDir,
    toggleSort,
    paginated,
    total,
    totalPages,
    from,
    to,
    resetFilters,
    hasActiveFilters,
    filtered,
  };
}
